var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { FocusEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
class BaseAutocompleteAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onBlur = new FocusEventListener();
        /** @event */
        this.onFocus = new FocusEventListener();
    }
    /**
     * Gets the expression for no results in the autocomplete.
     *
     * @returns the expression for no results in the autocomplete.
     */
    getEmptyResultExpression() {
        return this.state.emptyResultExpression;
    }
    /**
     * Sets the expression for no results in the autocomplete.
     *
     * @param emptyResultExpression - The expression for no results in the autocomplete.
     */
    setEmptyResultExpression(emptyResultExpression) {
        this.setState({ ...this.state, emptyResultExpression });
    }
    /**
     * Checks if the field is enabled.
     *
     * @returns a boolean indicating if the field is enabled.
     */
    isEnabled() {
        return this.state.enabled;
    }
    /**
     * Sets if the field is enabled.
     *
     * @param enabled - If the field is enabled.
     */
    setEnabled(enabled) {
        this.setState({ ...this.state, enabled });
    }
    /**
     * Gets the entry of the object used to get the pretty value.
     *
     * @returns the entry of the object used to get the pretty value.
     */
    getEntry() {
        return this.state.entry;
    }
    /**
     * Sets the entry of the object used to get the pretty value.
     *
     * @param entry - The entry of the object used to get the pretty value.
     */
    setEntry(entry) {
        this.setState({ ...this.state, entry });
    }
    /**
     * Gets the first page of the autocomplete results.
     *
     * @returns the first page of the autocomplete results.
     */
    getFirstPage() {
        return this.state.firstPage;
    }
    /**
     * Sets the first page of the autocomplete results.
     *
     * @param entry - The first page of the autocomplete results.
     */
    setFirstPage(firstPage) {
        this.setState({ ...this.state, firstPage });
    }
    /**
     * Gets the limit of items per page.
     *
     * @returns the limit of items per page.
     */
    getLimit() {
        return this.state.limit;
    }
    /**
     * Sets the limit of items per page.
     *
     * @param limit - The limit of items per page.
     */
    setLimit(limit) {
        this.setState({ ...this.state, limit });
    }
    /**
     * Gets the minimum length of characters to trigger the search.
     *
     * @returns the minimum length of characters to trigger the search.
     */
    getMinSearchLength() {
        return this.state.minSearchLength;
    }
    /**
    * Sets the minimum length of characters to trigger the search.
    *
    * @param minSearchLength - The minimum length of characters to trigger the search.
    */
    setMinSearchLength(minSearchLength) {
        this.setState({ ...this.state, minSearchLength });
    }
    /**
     * Gets the placeholder of the field.
     *
     * @returns the placeholder of the field.
     */
    getPlaceholder() {
        return this.state.placeholder;
    }
    /**
    * Sets the placeholder of the field.
    *
    * @param placeholder - The placeholder of the field.
    */
    setPlaceholder(placeholder) {
        this.setState({ ...this.state, placeholder });
    }
    /**
     * Gets the primary key of the options.
     *
     * @returns the primary key of the options.
     */
    getPrimaryKey() {
        return this.state.primaryKey;
    }
    /**
    * Sets the primary key of the options.
    *
    * @param primaryKey - The primary key of the options.
    */
    setPrimaryKey(primaryKey) {
        this.setState({ ...this.state, primaryKey });
    }
    /**
     * Gets the name of the key to identify the query parameter.
     *
     * @returns the name of the key to identify the query parameter.
     */
    getQueryKey() {
        return this.state.queryKey;
    }
    /**
    * Sets the name of the key to identify the query parameter.
    *
    * @param queryKey - The name of the key to identify the query parameter.
    */
    setQueryKey(queryKey) {
        this.setState({ ...this.state, queryKey });
    }
    /**
     * Checks if the field is read only.
     *
     * @returns a boolean indicating if the field is read only.
     */
    isReadOnly() {
        return this.state.readOnly ?? false;
    }
    /**
     * Sets if the field should be read only.
     *
     * @param readOnly - If the field should be read only.
     */
    setReadOnly(readOnly) {
        this.setState({ ...this.state, readOnly });
    }
    /**
     * Gets the title of the autocomplete modal.
     *
     * @returns the title of the autocomplete modal.
     */
    getTitle() {
        return this.state.title;
    }
    /**
    * Sets the title of the autocomplete modal.
    *
    * @param title - The title of the autocomplete modal.
    */
    setTitle(title) {
        this.setState({ ...this.state, title });
    }
    /**
     * Gets the data of the autocomplete.
     *
     * @returns the data of the autocomplete.
     */
    getData() {
        return this.state.data;
    }
    /**
    * Sets the data of the autocomplete.
    *
    * @param data - The data of the autocomplete.
    */
    setData(data) {
        this.setState({ ...this.state, data });
    }
    /**
     * Gets the maximum number of values formatted into the preview value.
     *
     * @returns the maximum number of values formatted into the preview value.
     */
    getPreviewAmount() {
        return this.state.previewAmount;
    }
    /**
    * Sets the maximum number of values formatted into the preview value.
    *
    * @param previewAmount - The maximum number of values formatted into the preview value.
    */
    setPreviewAmount(previewAmount) {
        this.setState({ ...this.state, previewAmount });
    }
    /**
     * Checks if the field can be cleared.
     *
     * @returns a boolean indicating if the field can be cleared.
     */
    getClearable() {
        return Boolean(this.state.clearable);
    }
    /**
     * Sets if the field should be clearable.
     *
     * @param clearable - If the field should be clearable.
     */
    setClearable(clearable) {
        this.setState({ ...this.state, clearable });
    }
    /**
     * Checks if the field value is being struckthrough.
     *
     * @returns a boolean indicating if the field value is being struckthrough.
     */
    isStrikethrough() {
        return Boolean(this.state.strikethrough);
    }
    /**
     * Sets if the field value should be struckthrough.
     *
     * @param strikethrough - If the field value should be struckthrough.
     */
    setStrikethrough(strikethrough) {
        this.setState({ ...this.state, strikethrough });
    }
    /**
     * Checks if the field allows multiselection.
     *
     * @returns a boolean indicating if the field allows multiselection.
     */
    isMultiselect() {
        return Boolean(this.state.multiselect);
    }
    /**
     * Sets if the field value should allow multiselection.
     *
     * @param multiselect - If the field value should allow multiselection.
     */
    setMultiselect(multiselect) {
        this.setState({ ...this.state, multiselect });
    }
    /**
     * Checks if the field options are from local data.
     *
     * @returns a boolean indicating if the field options are from local data.
     */
    isLocalData() {
        return Boolean(this.state.localData);
    }
    /**
     * Sets if the field options are from local data.
     *
     * @param localData - If the field options are from local data.
     */
    setLocalData(localData) {
        this.setState({ ...this.state, localData });
    }
    /**
     * Gets the configured sensitiveness for the field.
     *
     * @returns the configured sensitiveness for the field.
     */
    getSensitiveData() {
        return this.state.sensitiveData;
    }
    /**
     * Sets if the data of the field should be hidden when in read only mode.
     *
     * @param sensitiveData - How sensitive the data is.
     */
    setSensitiveData(sensitiveData) {
        this.setState({ ...this.state, sensitiveData });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], BaseAutocompleteAPI.prototype, "onBlur", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], BaseAutocompleteAPI.prototype, "onFocus", void 0);
export { BaseAutocompleteAPI, };
//# sourceMappingURL=base-autocomplete-api.js.map